/**
 * Corpus intake client (task 4020.2): batches, files, ZIP packages, and resumable uploads.
 *
 * Files go up in chunks through the offset protocol of /api/v1/intake/uploads: the server
 * acknowledges each chunk's end offset, so after a dropped connection or a page reload the upload
 * resumes from the acknowledged offset instead of starting over.
 */
import { ApiError, apiFetch } from "../../app/api";

export const CHUNK_BYTES = 4 * 1024 * 1024;
const MAX_RETRIES = 5;

export type IntakeMethod = "upload" | "folder" | "zip_package" | "manifest_batch";

export interface Rights {
  access_classification: "public" | "internal" | "confidential" | "restricted";
  permitted_processing_locations: string[];
  provider_exposure: "none" | "listed_providers" | "policy_permitted";
  permitted_providers: string[];
  verbatim_output_allowed: boolean;
  rag_corpus_allowed: boolean;
}

/** Source metadata and provider exposure per kind of content (task 4020.4). */
export interface ExposurePolicy {
  owner?: string;
  access_domain?: string;
  sensitivity_labels?: string[];
  revision_hint?: string;
  text_exposure: "allow" | "deny";
  image_exposure: "allow" | "deny";
  document_exposure: "allow" | "deny";
  redaction_profile?: string;
}

export interface OpenBatchRequest {
  engagement_id: string;
  batch_key: string;
  intake_method: IntakeMethod;
  default_rights: Rights;
  reason: string;
  run_id?: string;
  description?: string;
  manifest?: { format: "csv" | "json"; content: string };
  default_exposure?: ExposurePolicy;
}

export interface Batch {
  id: string;
  engagement_id: string;
  batch_key: string;
  intake_method: IntakeMethod;
  run_id: string | null;
  state: "open" | "sealed" | "failed";
  declared_file_count: number | null;
  failure_reason: string | null;
}

export interface Entry {
  entry_id: string;
  path: string;
  status: "accepted" | "duplicate" | "quarantined" | "rejected";
  sha256: string | null;
  reason: string | null;
  resumed?: boolean;
}

/** Lineage a manifest row declared (task 4020.6), and whether the seal could record it. */
export interface DeclaredLink {
  from_path: string;
  to_path: string;
  relationship: string;
  declared_as: string;
  row: number;
  status: "pending" | "asserted" | "unresolved";
  detail: string | null;
}

export interface Summary {
  batch: Batch;
  entries_by_status: Record<string, number>;
  uploads_in_progress: number;
  missing_declared: string[];
  inventory_sha256: string | null;
  declared_lineage?: DeclaredLink[];
}

export interface UploadState {
  id: string;
  path: string;
  size: number;
  offset: number;
  state: "receiving" | "complete" | "abandoned";
  entry: Entry | null;
}

const BINARY = { "Content-Type": "application/octet-stream" };

export function openBatch(request: OpenBatchRequest): Promise<Batch> {
  return apiFetch<Batch>("/api/v1/intake/batches", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export function getSummary(batchId: string): Promise<Summary> {
  return apiFetch<Summary>(`/api/v1/intake/batches/${batchId}`);
}

export function listEntries(batchId: string): Promise<Entry[]> {
  return apiFetch<Entry[]>(`/api/v1/intake/batches/${batchId}/entries`);
}

export function uploadPackage(batchId: string, path: string, file: Blob): Promise<Entry[]> {
  return apiFetch<Entry[]>(
    `/api/v1/intake/batches/${batchId}/packages?path=${encodeURIComponent(path)}`,
    { method: "PUT", body: file, headers: BINARY },
  );
}

export function sealBatch(batchId: string, reason: string, acceptMissing: boolean): Promise<Summary> {
  return apiFetch<Summary>(`/api/v1/intake/batches/${batchId}/seal`, {
    method: "POST",
    body: JSON.stringify({ reason, accept_missing: acceptMissing }),
  });
}

export function failBatch(batchId: string, reason: string): Promise<Batch> {
  return apiFetch<Batch>(`/api/v1/intake/batches/${batchId}/fail`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

/** The path a picked file keeps in the inventory: its folder-relative path when a folder was
 * chosen, otherwise its name. */
export function relativePath(file: File): string {
  const relative = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  return relative && relative.length > 0 ? relative : file.name;
}

function expectedOffset(error: unknown): number | null {
  if (error instanceof ApiError && error.status === 409) {
    const detail = error.detail as { expected_offset?: unknown } | null;
    if (detail && typeof detail.expected_offset === "number") return detail.expected_offset;
  }
  return null;
}

/**
 * Upload one file with resume. Starting an upload for a path already receiving returns the
 * server's acknowledged offset, so a re-run continues where the last one stopped.
 */
export async function uploadFile(
  batchId: string,
  path: string,
  file: Blob,
  onProgress: (sent: number, total: number) => void = () => undefined,
): Promise<Entry> {
  let upload = await apiFetch<UploadState>(`/api/v1/intake/batches/${batchId}/uploads`, {
    method: "POST",
    body: JSON.stringify({ path, size: file.size }),
  });
  let failures = 0;
  onProgress(upload.offset, file.size);
  while (upload.state === "receiving") {
    const end = Math.min(upload.offset + CHUNK_BYTES, file.size);
    try {
      upload = await apiFetch<UploadState>(`/api/v1/intake/uploads/${upload.id}`, {
        method: "PATCH",
        body: file.slice(upload.offset, end),
        headers: { ...BINARY, "Upload-Offset": String(upload.offset) },
      });
      failures = 0;
    } catch (error) {
      const resumeAt = expectedOffset(error);
      if (resumeAt !== null) {
        upload = { ...upload, offset: resumeAt };
        continue;
      }
      failures += 1;
      if (failures > MAX_RETRIES || (error instanceof ApiError && error.status < 500)) {
        throw error;
      }
      // Network failure or server error: ask where the server stands, then resume.
      upload = await apiFetch<UploadState>(`/api/v1/intake/uploads/${upload.id}`);
    }
    onProgress(upload.offset, file.size);
  }
  if (upload.state !== "complete" || upload.entry === null) {
    throw new Error(`upload of ${path} was ${upload.state}`);
  }
  return upload.entry;
}

// ---------------------------------------------------------------------------------------------
// Lineage review (task 4020.6): rulings on similarity candidates, origin marks, independence.
// ---------------------------------------------------------------------------------------------

export interface LineageAssertion {
  id: string;
  from_version_id: string;
  to_version_id: string;
  relationship: string;
  assertion_state: "candidate" | "confirmed" | "rejected";
  basis: string;
  origin_note: string | null;
  asserted_by: string;
  reason: string;
  asserted_at: string;
}

export interface CandidateRuling {
  id: string;
  from_version_id: string;
  to_version_id: string;
  kind: "near_duplicate" | "revision" | "supersession";
  similarity: number;
  basis: string;
  evidence: Record<string, unknown>;
  ai_advice: Record<string, unknown> | null;
  ruling_id: string | null;
  ruling_decision: "confirmed" | "rejected" | null;
  ruling_relationship: string | null;
  ruled_by: string | null;
  ruled_at: string | null;
  ruling_reason: string | null;
}

export interface VersionLineage {
  version_id: string;
  assertions: LineageAssertion[];
  candidates: CandidateRuling[];
  shares_origin_with: string[];
}

export interface Independence {
  independent_origins: number;
  groups: { versions: string[]; links: LineageAssertion[]; identical_bytes: string[][] }[];
  pending_candidates: CandidateRuling[];
  pending_assertions: LineageAssertion[];
}

export function getVersionLineage(versionId: string): Promise<VersionLineage> {
  return apiFetch<VersionLineage>(`/api/v1/lineage/versions/${versionId}`);
}

export function ruleCandidate(
  candidateId: string,
  ruling: { decision: "confirmed" | "rejected"; relationship?: string; reason: string },
): Promise<{ id: string; decision: string; relationship: string }> {
  return apiFetch(`/api/v1/lineage/candidates/${candidateId}/ruling`, {
    method: "POST",
    body: JSON.stringify(ruling),
  });
}

export function markOrigin(
  a: string,
  b: string,
  sameOrigin: boolean,
  reason: string,
): Promise<{ share_origin: boolean; still_linked_through: string[] }> {
  return apiFetch("/api/v1/lineage/origins", {
    method: "POST",
    body: JSON.stringify({ a, b, same_origin: sameOrigin, reason }),
  });
}

export function evaluateIndependence(versionIds: string[]): Promise<Independence> {
  return apiFetch<Independence>("/api/v1/lineage/independence", {
    method: "POST",
    body: JSON.stringify({ version_ids: versionIds }),
  });
}

// ---------------------------------------------------------------------------------------------
// File-level disposition (task 4020.7): every item with one category; operator actions; retries.
// ---------------------------------------------------------------------------------------------

export type DispositionCategory =
  | "processed"
  | "duplicate"
  | "excluded"
  | "failed"
  | "held"
  | "unresolved";

export interface FileDisposition {
  batch_id: string;
  entry_id: string;
  entry_seq: number;
  path: string;
  entry_status: Entry["status"];
  category: Exclude<DispositionCategory, "unresolved">;
  reason_code: string | null;
  reason: string | null;
  retryable: boolean;
  sha256: string | null;
  source_artifact_version_id: string | null;
  decided_by: string | null;
  decided_at: string | null;
}

export interface DispositionReport {
  scope: Record<string, unknown>;
  total: number;
  counts: Record<DispositionCategory, number>;
  files: FileDisposition[];
  unresolved: { batch_id: string; path: string; reason_code: string; reason: string }[];
  superseded: FileDisposition[];
  retryable_paths: string[];
}

export type FileAction = "release" | "reject" | "exclude" | "include";

export function getBatchDisposition(batchId: string): Promise<DispositionReport> {
  return apiFetch<DispositionReport>(`/api/v1/intake/batches/${batchId}/disposition`);
}

export function getRunDisposition(engagementId: string, runId: string): Promise<DispositionReport> {
  return apiFetch<DispositionReport>(
    `/api/v1/intake/runs/${runId}/disposition?engagement_id=${encodeURIComponent(engagementId)}`,
  );
}

export function actOnFile(
  entryId: string,
  action: FileAction,
  reason: string,
): Promise<{ state: string }> {
  return apiFetch(`/api/v1/intake/entries/${entryId}/${action}`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export function retryBatch(
  batchId: string,
  reason: string,
  batchKey?: string,
): Promise<{ batch: Batch; retry_paths: string[] }> {
  return apiFetch(`/api/v1/intake/batches/${batchId}/retry`, {
    method: "POST",
    body: JSON.stringify(batchKey ? { reason, batch_key: batchKey } : { reason }),
  });
}

// ---------------------------------------------------------------------------------------------
// Corpus inventory (task 4020.8): counts over the whole scope, filtered and paged rows.
// ---------------------------------------------------------------------------------------------

export interface InventoryItem {
  path: string;
  category: DispositionCategory;
  batch_id: string;
  batch_key: string;
  entry_id: string | null;
  entry_seq: number | null;
  entry_status: Entry["status"] | null;
  reason_code: string | null;
  reason: string | null;
  retryable: boolean;
  sha256: string | null;
  byte_size: number | null;
  media_type: string | null;
  source_artifact_version_id: string | null;
  title: string | null;
  duplicate_of_path: string | null;
  occurrences: number;
  access_classification: Rights["access_classification"] | null;
  provider_exposure: Rights["provider_exposure"] | null;
  rag_corpus_allowed: boolean | null;
  legal_hold: boolean | null;
  near_duplicate_candidates: number;
  revision_candidates: number;
  candidates_pending_review: number;
  lineage_links: number;
}

export interface BatchProgress {
  batch_id: string;
  batch_key: string;
  state: Batch["state"];
  declared_files: number | null;
  entries: number;
  uploads_in_progress: number;
  upload_bytes_received: number;
  upload_bytes_expected: number;
  bytes_stored: number;
  inventory_sha256: string | null;
}

export interface Inventory {
  scope: Record<string, unknown>;
  total: number;
  counts: Record<DispositionCategory, number>;
  facets: Record<string, Record<string, number>>;
  batches: BatchProgress[];
  filtered: number;
  offset: number;
  limit: number;
  superseded_attempts: number;
  items: InventoryItem[];
}

export interface InventoryQuery {
  batch_id?: string;
  engagement_id?: string;
  run_id?: string;
  category?: DispositionCategory;
  entry_status?: Entry["status"];
  access_classification?: Rights["access_classification"];
  pending_review?: boolean;
  retryable?: boolean;
  q?: string;
  offset?: number;
  limit?: number;
}

export function getInventory(query: InventoryQuery): Promise<Inventory> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  return apiFetch<Inventory>(`/api/v1/intake/inventory?${params.toString()}`);
}

/** Download link for a sealed batch's immutable inventory (same-origin cookie auth). */
export function manifestExportUrl(batchId: string, format: "csv" | "json"): string {
  return `/api/v1/intake/batches/${batchId}/manifest-export?format=${format}`;
}
